import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebouncedCallback } from "use-debounce";
import type { CashierTradingAccountMargin } from "@/api/cashier";
import { AccountSelector } from "@/components/account-list/account-selector/AccountSelector";
import { ErrorMessage } from "@/components/ErrorMessage";
import { ExchangerInput } from "@/components/exchanger/exchanger-input/ExchangerInput";
import { useAccount } from "@/hooks/transfer/useAccount";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import style from "./TransferExchanger.module.css";
import { Icon } from "@/components/general/icon/Icon";
import { EIcon } from "@/utils/enums";
import { Tooltip } from "@/components/tooltip/Tooltip";
import { useAmountForm } from "@/hooks/transfer/useAmountForm";
import type { TestDataProps } from "@repo/ui";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileAccountSelector } from "@/components/account-list/mobile-account-selector/MobileAccountSelector";
import { MobileAccountSelectorTrigger } from "@/components/account-list/mobile-account-selector-trigger/MobileAccountSelectorTrigger";
import { TriggerTypes } from "@/components/account-list/mobile-account-selector-trigger/MobileAccountSelectorTrigger.types";

type ExchangerProps = {
  tradingAccountList: CashierTradingAccountMargin[];
  tradingAccountToList: CashierTradingAccountMargin[];
  exchangeEventHandlers: {
    onProcessingAmountChangeStart: (amount: number) => Promise<{
      isSuccess: boolean;
      targetAmount: number;
    }>;
    onTradingAmountChangeStart: (amount: number) => Promise<{
      isSuccess: boolean;
      targetAmount: number;
    }>;
  };
  error?: {
    message: string;
    position: "top" | "bottom";
  } | null;
} & TestDataProps;

const DEBOUNCE_AMOUNT_MS = 300;

export const TransferExchanger = ({
  tradingAccountList,
  tradingAccountToList,
  exchangeEventHandlers,
  error,
  "data-testid": testId,
}: ExchangerProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const { updateAmountFormData } = useAmountForm();
  const isMobile = useIsMobile();

  const [processingAmount, setProcessingAmount] = useState("");
  const [tradingAmount, setTradingAmount] = useState("");
  const { formatBankingAmount } = useFormatAmount();
  const hasPreSelected = useRef(false);
  const [isOpen, setIsOpen] = useState<TriggerTypes | false>(false);

  const {
    setSelectedAccount,
    selectedAccount,
    selectedToAccount,
    setSelectedToAccount,
  } = useAccount();

  const selectedAccountCurrency = selectedAccount?.currency;
  const selectedAccountToCurrency = selectedToAccount?.currency;

  // Debounced Handlers
  const debouncedTradingAmountChange = useDebouncedCallback(
    async (amount: number) => {
      const res =
        await exchangeEventHandlers.onTradingAmountChangeStart(amount);
      if (res.isSuccess) {
        setProcessingAmount(String(res.targetAmount));
      }
    },
    DEBOUNCE_AMOUNT_MS
  );

  const debouncedProcessingAmountChange = useDebouncedCallback(
    async (amount: number) => {
      const res =
        await exchangeEventHandlers.onProcessingAmountChangeStart(amount);
      if (res.isSuccess) {
        setTradingAmount(String(res.targetAmount));
      }
    },
    DEBOUNCE_AMOUNT_MS
  );

  const cancelAllDebounced = useCallback(() => {
    debouncedTradingAmountChange.cancel();
    debouncedProcessingAmountChange.cancel();
  }, [debouncedTradingAmountChange, debouncedProcessingAmountChange]);

  // Input change handlers
  const handleTradingAmountChange = (amount: string) => {
    setTradingAmount(amount);
    cancelAllDebounced();
    debouncedTradingAmountChange(Number(amount));
  };

  const handleProcessingAmountChange = useCallback(
    (amount: string) => {
      setProcessingAmount(amount);
      cancelAllDebounced();
      debouncedProcessingAmountChange(Number(amount));
    },
    [cancelAllDebounced, debouncedProcessingAmountChange]
  );

  const handleSelectAccountFrom = (account: CashierTradingAccountMargin) => {
    if (selectedAccount && account.id === selectedToAccount?.id) {
      setSelectedToAccount(selectedAccount);
      setSelectedAccount(account);
    } else setSelectedAccount(account);

    if (selectedToAccount && tradingAmount) {
      handleTradingAmountChange(tradingAmount);
    }

    if (isMobile) {
      setIsOpen(false);
    }
  };

  const handleSelectAccountTo = (account: CashierTradingAccountMargin) => {
    setSelectedToAccount(account);
    if (selectedAccount && processingAmount) {
      handleProcessingAmountChange(processingAmount);
    }

    if (isMobile) {
      setIsOpen(false);
    }
  };

  const renderMobileBottomSheet = () => {
    switch (isOpen) {
      case TriggerTypes.TRANSFER_FROM:
        return (
          <MobileAccountSelector
            accounts={tradingAccountList || []}
            data-testid="transfer-account-selection-from"
            selectedAccount={selectedAccount}
            onAccountChange={handleSelectAccountFrom}
            onClose={() => setIsOpen(false)}
          />
        );
      case TriggerTypes.TRANSFER_TO:
        return (
          <MobileAccountSelector
            accounts={tradingAccountToList || []}
            data-testid="transfer-account-selection-to"
            selectedAccount={selectedToAccount}
            onAccountChange={handleSelectAccountTo}
            onClose={() => setIsOpen(false)}
            amountField="balance"
          />
        );
      default:
        break;
    }
  };

  const handleResetValues = useCallback(() => {
    if (tradingAccountList.length > 0 && !hasPreSelected.current) {
      updateAmountFormData({ processingValue: 0 });
      setSelectedAccount(tradingAccountList[0]);
      setSelectedToAccount(null);
      hasPreSelected.current = true;
    }
  }, [
    tradingAccountList,
    setSelectedAccount,
    setSelectedToAccount,
    updateAmountFormData,
  ]);

  useEffect(() => {
    handleResetValues();
  }, [handleResetValues]);
  console.log(processingAmount, selectedAccount);
  return (
    <div data-testid={testId}>
      <div className={style["transfer-form"]}>
        <div className={style["transfer-form-top-field"]}>
          <ExchangerInput
            testId="transfer-amount-input-from"
            value={processingAmount}
            onChange={handleProcessingAmountChange}
            selectedCurrency={selectedAccountCurrency}
            placeholder="0"
            disabled={!selectedAccount}
            processingCurrencies={
              selectedAccountCurrency ? [selectedAccountCurrency] : []
            }
            hasError={error?.position === "top" && processingAmount !== ""}
            subText={
              <div className={style["transfer-available-balance"]}>
                <span>
                  {t("withdrawals.methods.amount_input.available_balance", {
                    amount: selectedAccount
                      ? formatBankingAmount(selectedAccount.margin)
                      : "-",
                  })}
                </span>
                {selectedAccount && (
                  <Tooltip content={t("transfer.available_balance_info")}>
                    <Icon
                      className={style["transfer-available-balance-icon"]}
                      name={EIcon.InformationGray}
                    />
                  </Tooltip>
                )}
              </div>
            }
            topLabel={t("transfer.accounts.transfer_from")}
            topContent={
              !isMobile && (
                <AccountSelector
                  accounts={tradingAccountList || []}
                  data-testid="transfer-account-selection-from"
                  selectedAccount={selectedAccount}
                  onAccountChange={handleSelectAccountFrom}
                  menuOpenDirection="right"
                />
              )
            }
            locale={locale}
            mobileTrigger={
              isMobile && (
                <MobileAccountSelectorTrigger
                  selectedAccount={selectedAccount}
                  data-testid="transfer-account-selection-from"
                  handleTriggerClick={() =>
                    setIsOpen(TriggerTypes.TRANSFER_FROM)
                  }
                />
              )
            }
          />

          {error && error.position === "top" && (
            <ErrorMessage message={error.message} />
          )}
        </div>

        <ExchangerInput
          testId="transfer-amount-input-to"
          value={tradingAmount}
          onChange={handleTradingAmountChange}
          processingCurrencies={
            selectedAccountToCurrency ? [selectedAccountToCurrency] : []
          }
          disabled={!selectedToAccount}
          selectedCurrency={selectedAccountToCurrency}
          placeholder="0"
          subText={t("withdrawals.methods.amount_input.available_balance", {
            amount: selectedToAccount
              ? formatBankingAmount(selectedToAccount.balance)
              : "-",
          })}
          topLabel={t("transfer.accounts.transfer_to")}
          topContent={
            !isMobile && (
              <AccountSelector
                accounts={tradingAccountToList || []}
                data-testid="transfer-account-selection-to"
                selectedAccount={selectedToAccount}
                onAccountChange={handleSelectAccountTo}
                menuOpenDirection="right"
                amountField="balance"
              />
            )
          }
          hasError={!!error && tradingAmount !== ""}
          locale={locale}
          mobileTrigger={
            isMobile && (
              <MobileAccountSelectorTrigger
                selectedAccount={selectedToAccount}
                data-testid="transfer-account-selection-to"
                handleTriggerClick={() => setIsOpen(TriggerTypes.TRANSFER_TO)}
              />
            )
          }
        />
        {isMobile && renderMobileBottomSheet()}
      </div>

      {error && error.position === "bottom" && (
        <ErrorMessage message={error.message} />
      )}
    </div>
  );
};
